// src/services/stockService.js
/**
 * Stock Management Service - Comprehensive stock tracking for venues
 * 
 * Provides:
 * - Stock CRUD operations
 * - Low stock alerts
 * - Stock movement tracking
 * - Batch operations
 * - Real-time inventory updates
 * - Stock history and analytics
 * - Reorder point management
 * - Multi-venue stock tracking
 */

import { db } from './firebase';
import {
    collection,
    doc,
    query,
    where,
    orderBy,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    serverTimestamp,
    Timestamp,
    writeBatch,
    increment,
    limit as firestoreLimit
} from 'firebase/firestore';
import venuesService, { COLLECTIONS as VENUE_COLLECTIONS } from './venuesService';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

export const COLLECTIONS = {
    VENUE_STOCK: 'venueStock',
    STOCK_MOVEMENTS: 'stockMovements',
    STOCK_ALERTS: 'stockAlerts'
};

export const MOVEMENT_TYPE = {
    ADJUSTMENT: 'adjustment',
    DELIVERY: 'delivery',
    SALE: 'sale',
    RETURN: 'return',
    TRANSFER: 'transfer',
    WASTE: 'waste',
    RECOUNT: 'recount'
};

export const ALERT_TYPE = {
    LOW_STOCK: 'low_stock',
    OUT_OF_STOCK: 'out_of_stock',
    EXPIRING: 'expiring',
    EXPIRED: 'expired'
};

// Cache TTL
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes

// ═══════════════════════════════════════════════════════════════════════════════
// CACHE MANAGER
// ═══════════════════════════════════════════════════════════════════════════════

class CacheManager {
    constructor() {
        this.cache = new Map();
        this.timestamps = new Map();
    }

    set(key, value) {
        this.cache.set(key, value);
        this.timestamps.set(key, Date.now());
    }

    get(key) {
        if (!this.cache.has(key)) return null;
        
        const timestamp = this.timestamps.get(key);
        if (Date.now() - timestamp > CACHE_TTL) {
            this.delete(key);
            return null;
        }
        
        return this.cache.get(key);
    }

    delete(key) {
        this.cache.delete(key);
        this.timestamps.delete(key);
    }

    clear() {
        this.cache.clear();
        this.timestamps.clear();
    }

    has(key) {
        if (!this.cache.has(key)) return false;
        const timestamp = this.timestamps.get(key);
        if (Date.now() - timestamp > CACHE_TTL) {
            this.delete(key);
            return false;
        }
        return true;
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// STOCK SERVICE CLASS
// ═══════════════════════════════════════════════════════════════════════════════

class StockService {
    constructor() {
        this.cache = new CacheManager();
        this.subscriptions = new Map();
    }

    // ───────────────────────────────────────────────────────────────────────────
    // VALIDATION
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Validate stock item data
     */
    validateStockData(data) {
        const errors = [];

        if (!data.name || typeof data.name !== 'string' || !data.name.trim()) {
            errors.push('Item name is required');
        }

        if (!data.venueId) {
            errors.push('Venue ID is required');
        }

        if (data.currentStock !== undefined && (isNaN(data.currentStock) || data.currentStock < 0)) {
            errors.push('Current stock must be a non-negative number');
        }

        if (data.minimumLevel !== undefined && (isNaN(data.minimumLevel) || data.minimumLevel < 0)) {
            errors.push('Minimum level must be a non-negative number');
        }

        if (data.unitPrice !== undefined && (isNaN(data.unitPrice) || data.unitPrice < 0)) {
            errors.push('Unit price must be a non-negative number');
        }

        if (errors.length > 0) {
            throw new Error(`Validation failed: ${errors.join(', ')}`);
        }

        return true;
    }

    /**
     * Sanitize stock data
     */
    sanitizeStockData(data) {
        return {
            venueId: data.venueId,
            name: String(data.name || '').trim(),
            code: String(data.code || '').trim(),
            packSize: String(data.packSize || '').trim(),
            unitPrice: parseFloat(data.unitPrice) || 0,
            minimumLevel: parseInt(data.minimumLevel) || 0,
            reorderLevel: parseInt(data.reorderLevel) || 0,
            currentStock: parseInt(data.currentStock) || 0,
            category: String(data.category || '').trim(),
            supplier: String(data.supplier || '').trim(),
            location: String(data.location || '').trim(),
            notes: String(data.notes || '').trim(),
            tags: Array.isArray(data.tags) ? data.tags : [],
            metadata: data.metadata || {}
        };
    }

    // ───────────────────────────────────────────────────────────────────────────
    // STOCK OPERATIONS
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Get stock items for a venue
     */
    async getVenueStock(venueId, options = {}) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        const cacheKey = `stock_${venueId}_${JSON.stringify(options)}`;

        // Check cache
        if (!options.skipCache && this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        try {
            let q = query(
                collection(db, COLLECTIONS.VENUE_STOCK),
                where('venueId', '==', venueId)
            );

            // Apply filters
            if (options.category) {
                q = query(q, where('category', '==', options.category));
            }

            if (options.lowStockOnly) {
                // Filter for low stock items in post-processing
            }

            // Apply ordering
            const orderField = options.orderBy || 'name';
            const orderDirection = options.orderDirection || 'asc';
            q = query(q, orderBy(orderField, orderDirection));

            const snapshot = await getDocs(q);
            let stock = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.() || null,
                updatedAt: doc.data().updatedAt?.toDate?.() || null,
                isLowStock: doc.data().currentStock < doc.data().minimumLevel,
                isOutOfStock: doc.data().currentStock === 0
            }));

            // Post-process filters
            if (options.lowStockOnly) {
                stock = stock.filter(item => item.isLowStock);
            }

            // Cache results
            this.cache.set(cacheKey, stock);

            return stock;
        } catch (error) {
            console.error('Error fetching venue stock:', error);
            throw new Error(`Failed to fetch venue stock: ${error.message}`);
        }
    }

    /**
     * Get a single stock item
     */
    async getStockItem(stockId) {
        if (!stockId) {
            throw new Error('Stock ID is required');
        }

        const cacheKey = `stock_item_${stockId}`;

        // Check cache
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        try {
            const docRef = doc(db, COLLECTIONS.VENUE_STOCK, stockId);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                throw new Error('Stock item not found');
            }

            const item = {
                id: docSnap.id,
                ...docSnap.data(),
                createdAt: docSnap.data().createdAt?.toDate?.() || null,
                updatedAt: docSnap.data().updatedAt?.toDate?.() || null,
                isLowStock: docSnap.data().currentStock < docSnap.data().minimumLevel,
                isOutOfStock: docSnap.data().currentStock === 0
            };

            // Cache result
            this.cache.set(cacheKey, item);

            return item;
        } catch (error) {
            console.error('Error fetching stock item:', error);
            throw new Error(`Failed to fetch stock item: ${error.message}`);
        }
    }

    /**
     * Create a new stock item
     */
    async createStockItem(data) {
        try {
            // Validate
            this.validateStockData(data);

            // Sanitize
            const sanitizedData = this.sanitizeStockData(data);

            // Create document
            const docRef = await addDoc(collection(db, COLLECTIONS.VENUE_STOCK), {
                ...sanitizedData,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            // Clear cache
            this.cache.clear();

            // Check for alerts
            await this.checkAndCreateAlerts(docRef.id, sanitizedData);

            // Fetch and return the created item
            return await this.getStockItem(docRef.id);
        } catch (error) {
            console.error('Error creating stock item:', error);
            throw new Error(`Failed to create stock item: ${error.message}`);
        }
    }

    /**
     * Update a stock item
     */
    async updateStockItem(stockId, data) {
        if (!stockId) {
            throw new Error('Stock ID is required');
        }

        try {
            // Get current item
            const currentItem = await this.getStockItem(stockId);

            // Sanitize update data
            const sanitizedData = this.sanitizeStockData({ ...currentItem, ...data });

            // Update document
            const docRef = doc(db, COLLECTIONS.VENUE_STOCK, stockId);
            await updateDoc(docRef, {
                ...sanitizedData,
                updatedAt: serverTimestamp()
            });

            // Clear cache
            this.cache.delete(`stock_item_${stockId}`);
            this.cache.clear();

            // Check for alerts
            await this.checkAndCreateAlerts(stockId, sanitizedData);

            // Fetch and return the updated item
            return await this.getStockItem(stockId);
        } catch (error) {
            console.error('Error updating stock item:', error);
            throw new Error(`Failed to update stock item: ${error.message}`);
        }
    }

    /**
     * Delete a stock item
     */
    async deleteStockItem(stockId) {
        if (!stockId) {
            throw new Error('Stock ID is required');
        }

        try {
            // Delete the stock item
            await deleteDoc(doc(db, COLLECTIONS.VENUE_STOCK, stockId));

            // Clear cache
            this.cache.delete(`stock_item_${stockId}`);
            this.cache.clear();

            return { success: true, stockId };
        } catch (error) {
            console.error('Error deleting stock item:', error);
            throw new Error(`Failed to delete stock item: ${error.message}`);
        }
    }

    /**
     * Find stock item by venue and code
     */
    async findStockItemByCode(venueId, code) {
        if (!venueId || !code) {
            return null;
        }

        try {
            const q = query(
                collection(db, COLLECTIONS.VENUE_STOCK),
                where('venueId', '==', venueId),
                where('code', '==', code)
            );
            const snapshot = await getDocs(q);
            if (snapshot.empty) {
                return null;
            }

            const docSnap = snapshot.docs[0];
            return {
                id: docSnap.id,
                ...docSnap.data(),
                createdAt: docSnap.data().createdAt?.toDate?.() || null,
                updatedAt: docSnap.data().updatedAt?.toDate?.() || null
            };
        } catch (error) {
            console.error('Error finding stock item by code:', error);
            return null;
        }
    }

    // ───────────────────────────────────────────────────────────────────────────
    // STOCK MOVEMENTS
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Record stock movement and update stock level
     */
    async recordMovement(stockId, movement) {
        if (!stockId) {
            throw new Error('Stock ID is required');
        }

        try {
            const {
                type = MOVEMENT_TYPE.ADJUSTMENT,
                quantity,
                reason = '',
                staffId = null,
                reference = null
            } = movement;

            if (!quantity || isNaN(quantity)) {
                throw new Error('Valid quantity is required');
            }

            // Get current stock item
            const item = await this.getStockItem(stockId);

            // Calculate new stock level
            const quantityChange = parseInt(quantity);
            const newStock = item.currentStock + quantityChange;

            if (newStock < 0) {
                throw new Error('Stock cannot be negative');
            }

            // Update stock level
            await updateDoc(doc(db, COLLECTIONS.VENUE_STOCK, stockId), {
                currentStock: newStock,
                updatedAt: serverTimestamp()
            });

            // Record movement
            await addDoc(collection(db, COLLECTIONS.STOCK_MOVEMENTS), {
                stockId,
                venueId: item.venueId,
                itemName: item.name,
                type,
                quantity: quantityChange,
                previousStock: item.currentStock,
                newStock,
                reason,
                staffId,
                reference,
                createdAt: serverTimestamp()
            });

            // Clear cache
            this.cache.delete(`stock_item_${stockId}`);
            this.cache.clear();

            // Check for alerts
            await this.checkAndCreateAlerts(stockId, { ...item, currentStock: newStock });

            return {
                success: true,
                stockId,
                previousStock: item.currentStock,
                newStock,
                quantityChange
            };
        } catch (error) {
            console.error('Error recording movement:', error);
            throw new Error(`Failed to record movement: ${error.message}`);
        }
    }

    /**
     * Transfer stock between two venues.
     *
     * @param {string} fromVenueId - Source venue ID
     * @param {string} toVenueId - Destination venue ID
     * @param {string} sku - Product code/SKU
     * @param {number} units - Units to transfer
     * @param {object} options - { reason, userId }
     * @returns {Promise<object>} Result
     */
    async transferStockBetweenVenues(fromVenueId, toVenueId, sku, units, options = {}) {
        if (!fromVenueId || !toVenueId) throw new Error('Source and destination venue IDs required');
        if (!sku) throw new Error('SKU/code is required');
        const qty = Math.max(0, Number(units) || 0);
        if (qty <= 0) throw new Error('Units must be positive');

        const fromItem = await this.findStockItemByCode(fromVenueId, sku);
        if (!fromItem) throw new Error(`Stock item ${sku} not found at source venue`);
        if ((fromItem.currentStock || 0) < qty) {
            throw new Error(`Insufficient stock at source: ${fromItem.currentStock || 0} available, ${qty} requested`);
        }

        await this.recordMovement(fromItem.id, {
            type: MOVEMENT_TYPE.TRANSFER,
            quantity: -qty,
            reason: options.reason || `Transfer to venue ${toVenueId}`,
            reference: `transfer-to-${toVenueId}`
        });

        const toStock = await this.getVenueStock(toVenueId, { skipCache: true });
        const toItem = toStock.find(s => String(s.code) === String(sku));
        if (toItem) {
            await this.recordMovement(toItem.id, {
                type: MOVEMENT_TYPE.TRANSFER,
                quantity: qty,
                reason: options.reason || `Transfer from venue ${fromVenueId}`,
                reference: `transfer-from-${fromVenueId}`
            });
        } else {
            await this.createStockItem({
                venueId: toVenueId,
                code: sku,
                name: fromItem.name || sku,
                packSize: fromItem.packSize || '',
                unitPrice: fromItem.unitPrice || 0,
                minimumLevel: 0,
                currentStock: qty,
                category: fromItem.category || ''
            });
        }

        return { success: true, fromVenueId, toVenueId, sku, units: qty };
    }

    /**
     * Apply a stock take to venue stock levels
     */
    async applyStockTake(venueId, stockRecords = [], options = {}) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        if (!Array.isArray(stockRecords) || stockRecords.length === 0) {
            return {
                totalItems: Array.isArray(stockRecords) ? stockRecords.length : 0,
                applied: 0,
                unchanged: 0,
                notFound: 0,
                errors: [],
                details: []
            };
        }

        const summary = {
            totalItems: stockRecords.length,
            applied: 0,
            unchanged: 0,
            notFound: 0,
            errors: [],
            details: []
        };

        for (const record of stockRecords) {
            const targetValue = Number(
                record.actual ??
                record.actualStock ??
                record.quantity ??
                record.newStock ??
                0
            );

            if (!Number.isFinite(targetValue)) {
                summary.errors.push({
                    itemName: record.itemName || record.name || 'Unknown',
                    itemCode: record.itemCode || record.code || null,
                    error: 'Invalid actual stock value'
                });
                continue;
            }

            let stockItem = null;
            if (record.stockItemId) {
                try {
                    stockItem = await this.getStockItem(record.stockItemId);
                } catch (error) {
                    console.warn('[stockService] Could not load stock item by ID:', record.stockItemId, error);
                }
            }

            if (!stockItem) {
                const code = record.itemCode || record.code;
                if (code) {
                    stockItem = await this.findStockItemByCode(venueId, code);
                }
            }

            if (!stockItem || stockItem.venueId !== venueId) {
                summary.notFound += 1;
                summary.details.push({
                    itemName: record.itemName || record.name || 'Unknown',
                    itemCode: record.itemCode || record.code || null,
                    status: 'not_found'
                });
                continue;
            }

            if (stockItem.currentStock === targetValue) {
                summary.unchanged += 1;
                summary.details.push({
                    stockId: stockItem.id,
                    itemName: stockItem.name,
                    itemCode: stockItem.code,
                    previous: stockItem.currentStock,
                    newStock: targetValue,
                    change: 0,
                    status: 'unchanged'
                });
                continue;
            }

            const quantityChange = targetValue - stockItem.currentStock;

            try {
                const movement = await this.recordMovement(stockItem.id, {
                    type: MOVEMENT_TYPE.RECOUNT,
                    quantity: quantityChange,
                    reason: options.reason || 'Stock take approval',
                    staffId: options.approvedBy || null,
                    reference: options.referenceId || null
                });

                summary.applied += 1;
                summary.details.push({
                    stockId: stockItem.id,
                    itemName: stockItem.name,
                    itemCode: stockItem.code,
                    previous: movement.previousStock,
                    newStock: movement.newStock,
                    change: movement.quantityChange,
                    status: 'applied'
                });
            } catch (error) {
                console.error('Error applying stock take movement:', error);
                summary.errors.push({
                    stockId: stockItem.id,
                    itemName: stockItem.name,
                    itemCode: stockItem.code,
                    error: error.message
                });
            }
        }

        this.cache.clear();

        return summary;
    }

    /**
     * Get stock movements for an item
     */
    async getStockMovements(stockId, options = {}) {
        if (!stockId) {
            throw new Error('Stock ID is required');
        }

        try {
            let q = query(
                collection(db, COLLECTIONS.STOCK_MOVEMENTS),
                where('stockId', '==', stockId),
                orderBy('createdAt', 'desc')
            );

            if (options.limit) {
                q = query(q, firestoreLimit(options.limit));
            }

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.() || null
            }));
        } catch (error) {
            console.error('Error fetching stock movements:', error);
            throw new Error(`Failed to fetch stock movements: ${error.message}`);
        }
    }

    /**
     * Get stock movements for a venue by venueId and date range (for stock track over time).
     * Queries venue-level docs in stockMovements (venueId, type e.g. sale/delivery, quantity, previousStock, newStock).
     * @param {string} venueId - Venue ID
     * @param {object} options - { startDate, endDate, limit }
     * @param {string|Date} options.startDate - Start of range (YYYY-MM-DD or Date)
     * @param {string|Date} options.endDate - End of range (YYYY-MM-DD or Date)
     * @param {number} options.limit - Max movements to return (default 200)
     * @returns {Promise<Array>} Movements with id, venueId, stockId, type, quantity, previousStock, newStock, reason, reference, createdAt
     */
    async getVenueStockMovements(venueId, options = {}) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }
        const {
            startDate = null,
            endDate = null,
            limit: limitOption = 200
        } = options;

        try {
            let q = query(
                collection(db, COLLECTIONS.STOCK_MOVEMENTS),
                where('venueId', '==', venueId),
                orderBy('createdAt', 'desc')
            );

            if (startDate != null || endDate != null) {
                const start = startDate
                    ? (typeof startDate === 'string'
                        ? Timestamp.fromDate(new Date(startDate + 'T00:00:00Z'))
                        : Timestamp.fromDate(new Date(startDate)))
                    : Timestamp.fromDate(new Date(0));
                const end = endDate
                    ? (typeof endDate === 'string'
                        ? Timestamp.fromDate(new Date(endDate + 'T23:59:59.999Z'))
                        : Timestamp.fromDate(new Date(endDate)))
                    : Timestamp.now();
                if (startDate != null) q = query(q, where('createdAt', '>=', start));
                if (endDate != null) q = query(q, where('createdAt', '<=', end));
            }

            q = query(q, firestoreLimit(limitOption));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(docSnap => {
                const d = docSnap.data();
                const createdAt = d.createdAt?.toDate?.() || d.createdAt || null;
                return {
                    id: docSnap.id,
                    ...d,
                    createdAt,
                    date: createdAt ? createdAt.toISOString().slice(0, 10) : '',
                    at: createdAt ? createdAt.toISOString() : ''
                };
            });
        } catch (error) {
            console.error('Error fetching venue stock movements:', error);
            throw new Error(`Failed to fetch venue stock movements: ${error.message}`);
        }
    }

    /**
     * Batch update stock levels
     */
    async batchUpdateStock(updates) {
        if (!Array.isArray(updates) || updates.length === 0) {
            throw new Error('Updates array is required');
        }

        try {
            const batch = writeBatch(db);
            const results = [];

            for (const update of updates) {
                const { stockId, quantity, type, reason } = update;
                
                if (!stockId || !quantity) {
                    results.push({ stockId, success: false, error: 'Missing stockId or quantity' });
                    continue;
                }

                try {
                    const item = await this.getStockItem(stockId);
                    const quantityChange = parseInt(quantity);
                    const newStock = item.currentStock + quantityChange;

                    if (newStock < 0) {
                        results.push({ stockId, success: false, error: 'Stock cannot be negative' });
                        continue;
                    }

                    const stockRef = doc(db, COLLECTIONS.VENUE_STOCK, stockId);
                    batch.update(stockRef, {
                        currentStock: newStock,
                        updatedAt: serverTimestamp()
                    });

                    // Record movement
                    const movementRef = doc(collection(db, COLLECTIONS.STOCK_MOVEMENTS));
                    batch.set(movementRef, {
                        stockId,
                        venueId: item.venueId,
                        itemName: item.name,
                        type: type || MOVEMENT_TYPE.ADJUSTMENT,
                        quantity: quantityChange,
                        previousStock: item.currentStock,
                        newStock,
                        reason: reason || 'Batch update',
                        createdAt: serverTimestamp()
                    });

                    results.push({ stockId, success: true, newStock });
                } catch (error) {
                    results.push({ stockId, success: false, error: error.message });
                }
            }

            await batch.commit();

            // Clear cache
            this.cache.clear();

            return { success: true, results };
        } catch (error) {
            console.error('Error in batch update:', error);
            throw new Error(`Failed to batch update stock: ${error.message}`);
        }
    }

    /**
     * Batch set stock levels (sets to specific values)
     */
    async batchSetStockLevels(updates) {
        if (!Array.isArray(updates) || updates.length === 0) {
            throw new Error('Updates array is required');
        }

        try {
            const batch = writeBatch(db);
            const results = [];

            for (const update of updates) {
                const { stockId, currentStock, reason } = update;
                
                if (!stockId || currentStock === undefined) {
                    results.push({ stockId, success: false, error: 'Missing stockId or currentStock' });
                    continue;
                }

                try {
                    const item = await this.getStockItem(stockId);
                    const newStock = parseInt(currentStock);

                    if (newStock < 0) {
                        results.push({ stockId, success: false, error: 'Stock cannot be negative' });
                        continue;
                    }

                    const stockRef = doc(db, COLLECTIONS.VENUE_STOCK, stockId);
                    batch.update(stockRef, {
                        currentStock: newStock,
                        updatedAt: serverTimestamp()
                    });

                    // Record movement
                    const movementRef = doc(collection(db, COLLECTIONS.STOCK_MOVEMENTS));
                    batch.set(movementRef, {
                        stockId,
                        venueId: item.venueId,
                        itemName: item.name,
                        type: MOVEMENT_TYPE.ADJUSTMENT,
                        quantity: newStock - item.currentStock,
                        previousStock: item.currentStock,
                        newStock,
                        reason: reason || 'Stock level set',
                        createdAt: serverTimestamp()
                    });

                    results.push({ stockId, success: true, newStock });

                } catch (error) {
                    results.push({ stockId, success: false, error: error.message });
                }
            }

            await batch.commit();

            // Clear cache
            this.cache.clear();

            return { success: true, results };
        } catch (error) {
            console.error('Error in batch set stock levels:', error);
            throw new Error(`Failed to batch set stock levels: ${error.message}`);
        }
    }

    // ───────────────────────────────────────────────────────────────────────────
    // ALERTS
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Check stock levels and create alerts if needed
     */
    async checkAndCreateAlerts(stockId, itemData) {
        try {
            const { currentStock, minimumLevel, name, venueId } = itemData;

            // Clear existing alerts for this item
            const existingAlerts = query(
                collection(db, COLLECTIONS.STOCK_ALERTS),
                where('stockId', '==', stockId),
                where('resolved', '==', false)
            );
            const alertSnapshot = await getDocs(existingAlerts);
            
            const batch = writeBatch(db);
            alertSnapshot.docs.forEach(doc => {
                batch.update(doc.ref, { resolved: true, resolvedAt: serverTimestamp() });
            });

            // Create new alerts if needed
            if (currentStock === 0) {
                const alertRef = doc(collection(db, COLLECTIONS.STOCK_ALERTS));
                batch.set(alertRef, {
                    stockId,
                    venueId,
                    itemName: name,
                    type: ALERT_TYPE.OUT_OF_STOCK,
                    message: `${name} is out of stock`,
                    currentStock,
                    minimumLevel,
                    severity: 'critical',
                    resolved: false,
                    createdAt: serverTimestamp()
                });
            } else if (currentStock < minimumLevel) {
                const alertRef = doc(collection(db, COLLECTIONS.STOCK_ALERTS));
                batch.set(alertRef, {
                    stockId,
                    venueId,
                    itemName: name,
                    type: ALERT_TYPE.LOW_STOCK,
                    message: `${name} is below minimum level (${currentStock} / ${minimumLevel})`,
                    currentStock,
                    minimumLevel,
                    severity: 'warning',
                    resolved: false,
                    createdAt: serverTimestamp()
                });
            }

            await batch.commit();
        } catch (error) {
            console.error('Error checking alerts:', error);
        }
    }

    /**
     * Get active alerts for a venue
     */
    async getVenueAlerts(venueId, options = {}) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        try {
            let q = query(
                collection(db, COLLECTIONS.STOCK_ALERTS),
                where('venueId', '==', venueId),
                where('resolved', '==', false),
                orderBy('createdAt', 'desc')
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.() || null
            }));
        } catch (error) {
            console.error('Error fetching venue alerts:', error);
            throw new Error(`Failed to fetch venue alerts: ${error.message}`);
        }
    }

    // ───────────────────────────────────────────────────────────────────────────
    // STATISTICS
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Get stock statistics for a venue
     */
    async getVenueStockStats(venueId) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        try {
            const stock = await this.getVenueStock(venueId);
            const alerts = await this.getVenueAlerts(venueId);

            const stats = {
                totalItems: stock.length,
                lowStockCount: stock.filter(item => item.isLowStock).length,
                outOfStockCount: stock.filter(item => item.isOutOfStock).length,
                totalValue: stock.reduce((sum, item) => 
                    sum + (item.currentStock * item.unitPrice), 0
                ),
                categories: {},
                activeAlerts: alerts.length
            };

            // Count by category
            stock.forEach(item => {
                const category = item.category || 'Uncategorized';
                stats.categories[category] = (stats.categories[category] || 0) + 1;
            });

            return stats;
        } catch (error) {
            console.error('Error getting stock stats:', error);
            throw new Error(`Failed to get stock stats: ${error.message}`);
        }
    }

    // ───────────────────────────────────────────────────────────────────────────
    // SUBSCRIPTIONS
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Subscribe to venue stock changes
     */
    subscribeToVenueStock(venueId, callback) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        const subscriptionKey = `stock_${venueId}`;

        const unsubscribe = onSnapshot(
            query(
                collection(db, COLLECTIONS.VENUE_STOCK),
                where('venueId', '==', venueId),
                orderBy('name')
            ),
            (snapshot) => {
                const stock = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate?.() || null,
                    updatedAt: doc.data().updatedAt?.toDate?.() || null,
                    isLowStock: doc.data().currentStock < doc.data().minimumLevel,
                    isOutOfStock: doc.data().currentStock === 0
                }));

                // Update cache
                this.cache.set(subscriptionKey, stock);

                callback(stock);
            },
            (error) => {
                console.error('Error in stock subscription:', error);
                callback(null, error);
            }
        );

        this.subscriptions.set(subscriptionKey, unsubscribe);

        return unsubscribe;
    }

    /**
     * Subscribe to stock alerts
     */
    subscribeToAlerts(venueId, callback) {
        if (!venueId) {
            throw new Error('Venue ID is required');
        }

        const subscriptionKey = `alerts_${venueId}`;

        const unsubscribe = onSnapshot(
            query(
                collection(db, COLLECTIONS.STOCK_ALERTS),
                where('venueId', '==', venueId),
                where('resolved', '==', false),
                orderBy('createdAt', 'desc')
            ),
            (snapshot) => {
                const alerts = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate?.() || null
                }));

                callback(alerts);
            },
            (error) => {
                console.error('Error in alerts subscription:', error);
                callback(null, error);
            }
        );

        this.subscriptions.set(subscriptionKey, unsubscribe);

        return unsubscribe;
    }

    // ───────────────────────────────────────────────────────────────────────────
    // CLEANUP
    // ───────────────────────────────────────────────────────────────────────────

    /**
     * Cleanup all subscriptions and cache
     */
    cleanup() {
        this.subscriptions.forEach(unsubscribe => unsubscribe());
        this.subscriptions.clear();
        this.cache.clear();
    }

    unsubscribe(key) {
        if (this.subscriptions.has(key)) {
            this.subscriptions.get(key)();
            this.subscriptions.delete(key);
        }
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// SINGLETON INSTANCE
// ═══════════════════════════════════════════════════════════════════════════════

const stockServiceInstance = new StockService();

export default stockServiceInstance;

// Export the class for testing
export { StockService };

